import { ReactElement } from "react";
import { Button, Box, Text, Flex } from "@chakra-ui/react";
import { useCountdownLogout } from "../../../../hooks/useCountdownLogout";
import { useAssertEngineTypeFromPathParam } from "../../../../utils/selectAnalysisEngine";
import { employeeInfoAtom, outputStenScoreAtom } from "../../../../store";
import { useAtom } from "jotai";
import { LargeRadialChart } from "../../../../components/atoms/ResultCommon/RadialChart";
import { APP_TYPE } from "../../../../environments";
import { useModifiedTranslation } from "../../../../hooks/useModifiedTranslation";
import { PocResult } from "../../../../components/molecules/PocResult";
import { KokoroSvg } from "../../../../components/atoms/ResultCommon/KokoroSvg";
import { BasicPageLayout } from "../../../../components/templates/BasicPageLayout";
import { useColorToken } from "../../../../hooks/useColorToken";

export function AnalysisFailed(): ReactElement {
  const t = useModifiedTranslation();
  useAssertEngineTypeFromPathParam();
  const { countdown, logout } = useCountdownLogout(3);
  const [outputStenScore] = useAtom(outputStenScoreAtom);

  const vitalityColor = useColorToken("vitality.primary");
  const mentalActivityColor = useColorToken("mentalActivity.primary");

  const [employeeInfo] = useAtom(employeeInfoAtom);

  return (
    <BasicPageLayout
      title={employeeInfo.employee_name + t("Graph.common.personTitle")}
      minH="full"
      gap={2}
    >
      {APP_TYPE === "poc" && <PocResult />}
      {APP_TYPE !== "poc" && (
        <>
          <Flex
            justifyContent="center"
            alignItems="end"
            fontFamily="Oswald"
            fontWeight="500"
            marginBottom={6}
          >
            <Text
              textAlign="end"
              fontSize="clamp(1rem, 4vw, 1.5rem)"
              color="red.500"
              marginBottom="3px"
            >
              {t("Result.analysis_failed")}
            </Text>
          </Flex>

          {outputStenScore && (
            <>
              <KokoroSvg imageIndex={0} />

              <Flex
                bg="common.base"
                w="full"
                maxW="100%"
                justifyContent="space-around"
                alignItems="end"
                p={5}
              >
                <Box>
                  {/* こころ指数 */}
                  <Text fontSize="clamp(2rem, 5vw, 3rem)">
                    {t("Graph.common.mi1Result")}
                  </Text>
                  {/* 個人最適化した10段階評価 */}
                  <Text
                    fontSize="clamp(0.75rem, 4vw, 1.25rem)"
                    fontWeight="normal"
                  >
                    {t("Graph.common.mi1ResultExplanation")}
                  </Text>
                </Box>
                <Box>
                  {/* こころ指数 */}
                  <Text textAlign="center" fontSize="clamp(2rem, 5vw, 4.5rem)">
                    {t("Result.error_value")}
                  </Text>
                  {/* 個人最適化した10段階評価 */}
                  <Text
                    textAlign="center"
                    fontSize="clamp(0.75rem, 4vw, 1.25rem)"
                    whiteSpace="nowrap"
                  >
                    [1-10]
                  </Text>
                </Box>
              </Flex>
            </>
          )}
          {!outputStenScore && (
            <>
              <Flex p="6" w="full" maxW="100%">
                <Box
                  bg="common.base"
                  boxShadow="md"
                  rounded="xl"
                  paddingTop="3"
                  width="100%"
                >
                  {/* 元気圧 */}
                  <LargeRadialChart
                    progress={0}
                    barColor={vitalityColor}
                    score={t("Result.error_value")}
                    title={t("Graph.common.vitality")}
                    trend={undefined}
                  />
                </Box>
              </Flex>
              <Box p="6">
                <Flex w="full" maxW="100%">
                  <Box
                    bg="common.base"
                    boxShadow="md"
                    rounded="xl"
                    paddingTop="3"
                    width="100%"
                  >
                    {/* 心の活量値 */}
                    <LargeRadialChart
                      progress={0}
                      barColor={mentalActivityColor}
                      score={t("Result.error_value")}
                      title={t("Graph.common.mentalActivity")}
                      trend={undefined}
                    />
                  </Box>
                </Flex>
              </Box>
            </>
          )}
        </>
      )}
      <Button
        onClick={logout}
        size="lg"
        variant="btn_primary"
        width="100%"
        minHeight="64px"
        mt="auto"
      >
        {t("QueryLogin.common.resultTitle")} {countdown}
      </Button>
    </BasicPageLayout>
  );
}
